import _debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { uploadImage } from '../../../api/api.global';
import { getApi, postApi } from '../../../api/call.api';
import { urlApi } from '../../../api/url.api';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';

const CardDesignTab = ({ state, pageId, callback }) => {

    const [pageData, setPageData] = useState(state);
    const [templates, setTemplates] = useState([])

    useEffect(() => {
        setPageData(state || {});
        setSelectedBgMode(state?.backgroundOption?.backgroundType || 1);
    }, [state])

    const backgroundImageInputRef = useRef(null)

    const presets = [
        {
            id: 0,
            colors: ['#000000', '#C3C3C3']
        }, {
            id: 1,
            colors: ['#517AFA', '#C5FEFF']
        }, {
            id: 2,
            colors: ['#374793', '#C5FEFF']
        }, {
            id: 3,
            colors: ['#D51A47', '#FFB1DB']
        }, {
            id: 4,
            colors: ['#805F37', '#F85C8B']
        }, {
            id: 5,
            colors: ['#FF78A1', '#CCFFAC']
        }, {
            id: 6,
            colors: ['#469EA6', '#BAF9FF']
        }, {
            id: 7,
            colors: ['#194F92', '#C1DDFF']
        }, {
            id: 8,
            colors: ['#6474E5', '#CED4FF']
        }
    ];

    const fonts = [
        {
            id: 1,
            name: "Montserrat"
        }, {
            id: 2,
            name: 'Anton',
        }, {
            id: 3,
            name: 'Josefin Sans'
        }, {
            id: 4,
            name: 'Open Sans'
        }, {
            id: 5,
            name: 'Oswald'
        }, {
            id: 6,
            name: 'Titillium Web'
        }, {
            id: 7,
            name: 'Roboto'
        }, {
            id: 8,
            name: 'Raleway'
        }, {
            id: 9,
            name: 'Arvo'
        }, {
            id: 10,
            name: 'Cormorant Garamond'
        }, {
            id: 11,
            name: 'Libre Baskerville'
        }, {
            id: 12,
            name: 'Slabo'
        }, {
            id: 13,
            name: 'Amatic SC'
        }, {
            id: 14,
            name: 'Itim'
        }, {
            id: 15,
            name: 'Architects Daughter'
        }, {
            id: 16,
            name: 'Permanent Marker'
        }
    ];

    const [selectedBgMode, setSelectedBgMode] = useState(state?.backgroundOption?.backgroundType || 1);

    const getTemplatesList = async () => {
        let resp = await getApi(urlApi.getCardTemplates);
        if (resp?.responseCode === 200) {
            setTemplates(resp.data);
        } else {
            setTemplates([]);
        }
    }

    useEffect(() => {
        getTemplatesList();
    }, []);

    async function update(postDetails) {
        let postData = {
            linkId: pageId,
            postData: postDetails
        }
        let resp = await postApi(postData, urlApi.editDesignOfCard);
        if (resp?.responseCode === 200) {
            if (callback) {
                callback();
            }
        }
        if (resp.responseCode === 500) {
            toast.error(resp.message);
        }
    }

    const handlePresetClick = (item) => {
        let postData = [{
            key: 'backgroundOption.color',
            value: item?.colors[1]
        }, {
            key: 'backgroundOption.primaryColor',
            value: item?.colors[0]
        }, {
            key: 'backgroundOption.preset',
            value: item?.id
        }, {
            key: 'backgroundOption.backgroundType',
            value: 1
        }];
        update(postData);
    }

    const onBackgroundColorChange = (e) => {
        let postData = [{
            key: 'backgroundOption.color',
            value: e.target.value
        }, {
            key: 'backgroundOption.preset',
            value: 20
        }, {
            key: 'backgroundOption.backgroundType',
            value: 1
        }]
        update(postData);
    }

    const onPrimaryColorChange = (e) => {
        let postData = [{
            key: 'backgroundOption.primaryColor',
            value: e.target.value
        }, {
            key: 'backgroundOption.preset',
            value: 20
        }];
        update(postData);
    }

    const onTextColorChange = (e) => {
        let postData = [{
            key: 'textOption.titleColor',
            value: e.target.value
        }];
        update(postData);
    }

    const onDescriptionColorChange = (e) => {
        let postData = [{
            key: 'textOption.descriptionColor',
            value: e.target.value
        }];
        update(postData);
    }

    const onBackgroundImageChange = async (e) => {
        const file = e.target.files[0];
        const maxSizeInBytes = 1024 * 1024 * 5;
        if (file && file.size > maxSizeInBytes) {
            toast.error('File size exceeds the limit of 5MB.');
            e.target.value = null;
        } else {
            let imageResp = await uploadImage(file, 'advy-pages');
            let postData = [{
                key: 'backgroundOption.image',
                value: imageResp
            }, {
                key: 'backgroundOption.backgroundType',
                value: 3
            }];
            update(postData);
            e.target.value = null;
        }
    }

    const onBackgroundImageRemove = () => {
        let postData = [{
            key: 'backgroundOption.image',
            value: ''
        }, {
            key: 'backgroundOption.backgroundType',
            value: 1
        }];
        update(postData);
    }

    const onBlockStyleSelectionChange = (borderRadius, background) => {
        let postData = [{
            key: 'blocksOption.borderRadius',
            value: borderRadius
        }, {
            key: 'blocksOption.background',
            value: background
        }];
        update(postData);
    };

    const onBlockShadowSelect = (shadowSize) => {
        let postData = [{
            key: 'blocksOption.shadowSize',
            value: shadowSize
        }];
        update(postData);
    }

    const onBlockBorderSelect = (borderSize) => {
        let postData = [{
            key: 'blocksOption.border',
            value: borderSize
        }];
        update(postData);
    }

    const onBlockTitleColorChange = (e) => {
        let postData = [{
            key: 'blocksOption.titleColor',
            value: e.target.value
        }];
        update(postData);
    };

    const onBlockBgColorChange = (e) => {
        let postData = [{
            key: 'blocksOption.background',
            value: e.target.value
        }];
        update(postData);
    }

    const onBlockBorderColorChange = (e) => {
        let postData = [{
            key: 'blocksOption.borderColor',
            value: e.target.value
        }];
        update(postData);
    };

    const onBlockDescriptionColorChange = (e) => {
        let postData = [{
            key: 'blocksOption.descriptionColor',
            value: e.target.value
        }];
        update(postData);
    };

    const onFontSelect = (font) => {
        let postData = [{
            key: 'textOption.font',
            value: font.name
        }];
        update(postData);
    }

    const handleLayoutClick = (item) => {
        let postData = [{
            key: 'templateNumber',
            value: item.cardNumber
        }];
        update(postData);
    }

    return (
        <>
            <div className="col-span-3 md:col-span-2">

                {/* theme */}
                <div className="border border-1 border-[#ECECF2] p-2 rounded-xl mb-3">
                    <p className='text-sm font-bold'>Presets</p>

                    <div className='flex gap-3 mb-4 flex-wrap'>
                        {
                            presets.map((item) => {
                                return (
                                    <div key={item?.id} className={`p-1  rounded-full ${pageData?.backgroundOption?.preset == item?.id && 'outline outline-2 outline-primary'}`}>
                                        <div className={`h-12 shadow aspect-square cursor-pointer rounded-full overflow-hidden`}
                                            onClick={() => handlePresetClick(item)}>
                                            <div className="h-7 w-full" style={{ backgroundColor: item.colors[0] }}></div>
                                            <div className="h-5 w-full" style={{ backgroundColor: item.colors[1] }}></div>
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>

                    <p className='text-sm font-bold'>Layouts</p>
                    <div className='flex gap-3 mb-4 flex-wrap'>
                        {
                            templates.map((item) => {
                                return (
                                    <div key={item?.templateNumber} className={`p-1 rounded-lg shadow-md ${pageData?.templateNumber == item?.cardNumber && 'border-2 border-primary'}`}>
                                        <div className={`h-[210px] shadow cursor-pointer rounded-md overflow-hidden`}
                                            onClick={() => handleLayoutClick(item)}>
                                            <img src={item?.cardImage} alt="background" className="h-full w-full object-cover" />
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>

                    <p className='text-sm font-bold mt-3 mb-2'>Primary Color</p>
                    <div className='flex !mb-3'>
                        <input type="color" className='h-8 w-8 rounded-s-lg focus-within:outline-none border-none focus:outline-none focus:border-none' name="" id="" onChange={_debounce((e) => onPrimaryColorChange(e), 300)} defaultValue={pageData?.backgroundOption?.primaryColor} key={pageData?.backgroundOption?.primaryColor || 'primary-color'} />
                        <input type="text" className='text-black rounded-e-lg bg-[#E8EBF2] p-1 text-sm focus:outline-none' name="" id="" onBlur={(e) => onPrimaryColorChange(e)} defaultValue={pageData?.backgroundOption?.primaryColor || "#000000"} key={"primary-" + pageData?.backgroundOption?.primaryColor} />
                    </div>

                    {/* background mode start */}
                    <div className="flex items-center justify-between mb-3">
                        <p className='text-sm font-bold mb-0'>Background</p>
                        <div className="flex gap-3 bg-[#F4F6FA] px-4 py-1.5 rounded-full">
                            <button className={`px-4 text-sm font-semibold py-1 rounded-full ${selectedBgMode == 1 ? 'bg-white text-[#273144]' : 'bg-transparent text-[#526281]'}`} onClick={() => setSelectedBgMode(1)}>Color</button>
                            <button className={`px-4 text-sm font-semibold py-1 rounded-full ${selectedBgMode == 3 ? 'bg-white text-[#273144]' : 'bg-transparent text-[#526281]'}`} onClick={() => setSelectedBgMode(3)}>Image</button>
                        </div>
                    </div>

                    {
                        selectedBgMode === 1 && (
                            <div className='flex'>
                                <input type="color" className='h-8 w-8 rounded-s-lg focus-within:outline-none border-none focus:outline-none focus:border-none' name="" id="" onChange={_debounce((e) => onBackgroundColorChange(e), 300)} defaultValue={pageData?.backgroundOption?.color} key={pageData?.backgroundOption?.color} />
                                <input type="text" className='text-black rounded-e-lg bg-[#E8EBF2] p-1 text-sm focus:outline-none' name="" id="" onBlur={(e) => onBackgroundColorChange(e)} defaultValue={pageData?.backgroundOption?.color || "#000000"} key={"input-bg-" + pageData?.backgroundOption?.color} />
                            </div>
                        )
                    }

                    {
                        selectedBgMode === 3 && (
                            <div className="flex flex-wrap gap-3 items-center">
                                <input type="file" accept="image/*" name="" id="" className="hidden" onChange={onBackgroundImageChange} ref={backgroundImageInputRef} />
                                <div> <img src={pageData?.backgroundOption?.image ? (pageData?.backgroundOption?.image) : (process.env.PUBLIC_URL + "/images/image-upload.png")} className='h-20 aspect-square rounded-lg object-cover' alt="" />
                                    <p className="text-xs text-gray-400">*Maximum size: 5MB & preferred ratio is 9:16</p>
                                </div>
                                <button onClick={() => backgroundImageInputRef.current.click()} className='rounded-full px-3 py-1 font-semibold text-sm text-primary border-2 border-primary h-max'>Upload Image</button>
                                <ConfirmationModal buttonComponent={<button className='rounded-full px-3 py-1 font-semibold text-sm text-[var(--secondary)] border-2 border-secondary h-max'>Remove</button>} confirmationText={"Are you sure want to remove background image ?"} onConfirm={() => onBackgroundImageRemove()} />

                            </div>
                        )
                    }
                    {/* background mode end */}


                    <p className='text-sm font-bold mt-3 mb-1'>Text Color</p>
                    <div className="flex flex-wrap gap-5">
                        <div>
                            <p className='text-sm font-semibold mb-1 text-[#273144]'>Title</p>
                            <div className='flex'>
                                <input type="color" className='h-8 w-8 rounded-s-lg outline-none border-none focus:outline-none focus:border-none' name="" id="" onChange={_debounce((e) => onTextColorChange(e), 300)} defaultValue={pageData?.textOption?.titleColor} key={`input-title-header-${pageData?.textOption?.titleColor}`} />
                                <input type="text" className='text-black rounded-e-lg bg-[#E8EBF2] p-1 text-sm focus:outline-none' name="" id="" onBlur={(e) => onTextColorChange(e)} defaultValue={pageData?.textOption?.titleColor} key={pageData?.textOption?.titleColor} />
                            </div>
                        </div>
                        <div>
                            <p className='text-sm font-semibold mb-1 text-[#273144]'>Headings</p>
                            <div className='flex'>
                                <input type="color" className='h-8 w-8 rounded-s-lg outline-none border-none focus:outline-none focus:border-none' name="" id="" onChange={_debounce((e) => onDescriptionColorChange(e), 300)} defaultValue={pageData?.textOption?.descriptionColor} key={`input-title-description-${pageData?.textOption?.descriptionColor}`} />
                                <input type="text" className='text-black rounded-e-lg bg-[#E8EBF2] p-1 text-sm focus:outline-none' name="" id="" onBlur={(e) => onDescriptionColorChange(e)} defaultValue={pageData?.textOption?.descriptionColor} key={pageData?.textOption?.descriptionColor} />
                            </div>
                        </div>
                    </div>

                </div>

                {/* socials */}
                {/* <div className="border border-1 border-[#ECECF2] p-2 rounded-xl mb-3">
                    <p className='text-sm font-bold'>Socials</p>
                    <p className='text-sm font-bold mt-3'>Style</p>
                    <div className='flex items-center gap-5'>
                         <div>
                            <p className='text-sm font-semibold mb-1 text-[#273144]'>Icon Color</p>
                            <div className="flex gap-3 flex-wrap">
                                <input type="radio" id="radio-1" name="icon-color-radio-btn" value="radio-1" />
                                <label htmlFor="radio-1">Original</label>

                                <input type="radio" id="radio-2" name="icon-color-radio-btn" value="radio-2" />
                                <label htmlFor="radio-2">Black</label>

                                <input type="radio" id="radio-3" name="icon-color-radio-btn" value="radio-3" />
                                <label htmlFor="radio-3">White</label>
                            </div>
                        </div> 
                        <div>
                            <p className='text-sm font-semibold mb-1 text-[#273144]'>Placement</p>
                            <div className="flex gap-3 flex-wrap">
                                <input type="radio" id="radio-1" name="icon-color-radio-btn" value="top" onChange={onSocialIconPositionChange} defaultChecked={pageData?.socialLinksOption?.placement == 'top'} />
                                <label htmlFor="radio-1">Top Of Page</label>

                                <input type="radio" id="radio-2" name="icon-color-radio-btn" value="bottom" onChange={onSocialIconPositionChange} defaultChecked={pageData?.socialLinksOption?.placement == 'bottom'} />
                                <label htmlFor="radio-2">Bottom Of Page</label>
                            </div>
                        </div>
                    </div>
                </div> */}

                {/* blocks */}
                <div className="border border-1 border-[#ECECF2] p-2 rounded-xl mb-3">
                    <p className='text-sm font-bold'>Blocks</p>
                    <p className='text-sm font-semibold text-[#273144]'>Shape</p>

                    <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className={`h-12 rounded-full w-full bg-[#B1B3C8] cursor-pointer ${(pageData?.blocksOption?.background != 'transparent' && pageData?.blocksOption?.borderRadius == "35px") ? 'border-2 border-primary' : ''}`} onClick={() => onBlockStyleSelectionChange('35px', (pageData?.blocksOption?.background == 'transparent' ? '#B1B3C8' : pageData?.blocksOption?.background))}></div>

                        <div className={`h-12 rounded-lg w-full bg-[#B1B3C8] cursor-pointer ${(pageData?.blocksOption?.background != 'transparent' && pageData?.blocksOption?.borderRadius == "12px") ? 'border-2 border-primary' : ''}`} onClick={() => onBlockStyleSelectionChange('12px', (pageData?.blocksOption?.background == 'transparent' ? '#B1B3C8' : pageData?.blocksOption?.background))}></div>

                        <div className={`h-12 rounded-sm w-full bg-[#B1B3C8] cursor-pointer ${(pageData?.blocksOption?.background != 'transparent' && pageData?.blocksOption?.borderRadius == "2px") ? 'border-2 border-primary' : ''}`} onClick={() => onBlockStyleSelectionChange('2px', (pageData?.blocksOption?.background == 'transparent' ? '#B1B3C8' : pageData?.blocksOption?.background))}></div>

                        <div className={`h-12 rounded-full w-full border-2 ${(pageData?.blocksOption?.background == 'transparent' && pageData?.blocksOption?.borderRadius == "35px") ? 'border-primary' : 'border-[#B1B3C8]'} cursor-pointer`} onClick={() => onBlockStyleSelectionChange('35px', 'transparent')}></div>

                        <div className={`h-12 rounded-lg w-full border-2 ${(pageData?.blocksOption?.background == 'transparent' && pageData?.blocksOption?.borderRadius == "12px") ? 'border-primary' : 'border-[#B1B3C8]'} cursor-pointer`} onClick={() => onBlockStyleSelectionChange('12px', 'transparent')}></div>

                        <div className={`h-12 rounded-sm w-full border-2 ${(pageData?.blocksOption?.background == 'transparent' && pageData?.blocksOption?.borderRadius == "2px") ? 'border-primary' : 'border-[#B1B3C8]'} cursor-pointer`} onClick={() => onBlockStyleSelectionChange('2px', 'transparent')}></div>
                    </div>

                    <p className='text-sm font-semibold text-[#273144] mb-1'>Shadow</p>
                    <div className="flex gap-4 mb-4">
                        <div className={`h-16 w-40 border-2 rounded-lg ${pageData?.blocksOption?.shadowSize == "none" ? 'border-primary' : 'border-[#B1B3C8]'} cursor-pointer`} onClick={() => onBlockShadowSelect('none')}></div>
                        <div className={`h-16 w-40 border-2 rounded-lg ${pageData?.blocksOption?.shadowSize == "medium" ? 'border-primary' : 'border-[#B1B3C8]'} cursor-pointer`} onClick={() => onBlockShadowSelect('medium')} style={{ boxShadow: '3px 6px 2px 0px rgba(0,0,0,0.58)' }}></div>
                        <div className={`h-16 w-40 border-2 rounded-lg ${pageData?.blocksOption?.shadowSize == "large" ? 'border-primary' : 'border-[#B1B3C8]'} cursor-pointer`} onClick={() => onBlockShadowSelect('large')} style={{ boxShadow: '3px 6px 10px 0px rgba(0,0,0,0.58)' }}></div>
                    </div>

                    <p className='text-sm font-semibold text-[#273144] mb-1'>Border</p>
                    <div className="flex gap-4 mb-4">
                        <div className={`h-16 w-40 rounded-lg ${pageData?.blocksOption?.border == "0px" ? '!border-2 border-primary' : 'border-[#B1B3C8]'} shadow-md cursor-pointer`} title='No Border' onClick={() => onBlockBorderSelect('0px')}></div>
                        <div className={`h-16 w-40 border-2 rounded-lg ${pageData?.blocksOption?.border == "2px" ? 'border-primary' : 'border-'} shadow-md cursor-pointer`} onClick={() => onBlockBorderSelect('2px')}></div>
                        <div className={`h-16 w-40 border-[4px] rounded-lg ${pageData?.blocksOption?.border == "4px" ? 'border-primary' : 'border-[#B1B3C8]'} shadow-md cursor-pointer`} onClick={() => onBlockBorderSelect('4px')}></div>
                    </div>

                    <p className='text-sm font-bold'>Color</p>
                    <div className="flex !gap-5 flex-wrap">
                        <div>
                            <p className='text-sm font-semibold mb-1 text-[#273144]'>Block Title</p>
                            <div className='flex'>
                                <input type="color" className='h-8 w-8 rounded-s-lg outline-none border-none focus:outline-none focus:border-none' name="" id="" onChange={_debounce((e) => onBlockTitleColorChange(e), 300)} defaultValue={pageData?.blocksOption?.titleColor} key={pageData?.blocksOption?.titleColor} />
                                <input type="text" className='text-black rounded-e-lg bg-[#E8EBF2] p-1 text-sm focus:outline-none' name="" id="" onBlur={(e) => onBlockTitleColorChange(e)} defaultValue={pageData?.blocksOption?.titleColor} key={"input-title-block-" + pageData?.blocksOption?.titleColor} />
                            </div>
                        </div>
                        <div>
                            <p className='text-sm font-semibold mb-1 text-[#273144]'>Block Description</p>
                            <div className='flex'>
                                <input type="color" className='h-8 w-8 rounded-s-lg outline-none border-none focus:outline-none focus:border-none' name="" id="" onChange={_debounce((e) => onBlockDescriptionColorChange(e), 300)} key={pageData?.blocksOption?.descriptionColor} defaultValue={pageData?.blocksOption?.descriptionColor} />
                                <input type="text" className='text-black rounded-e-lg bg-[#E8EBF2] p-1 text-sm focus:outline-none' name="" id="" onBlur={(e) => onBlockDescriptionColorChange(e)} key={"input-description-block-" + pageData?.blocksOption?.descriptionColor} defaultValue={pageData?.blocksOption?.descriptionColor} />
                            </div>
                        </div>
                        {pageData?.blocksOption?.background != 'transparent' && <div>
                            <p className='text-sm font-semibold mb-1 text-[#273144]'>Block Background</p>
                            <div className='flex'>
                                <input type="color" className='h-8 w-8 rounded-s-lg outline-none border-none focus:outline-none focus:border-none' name="" id="" onChange={_debounce((e) => onBlockBgColorChange(e), 300)} defaultValue={pageData?.blocksOption?.background} key={pageData?.blocksOption?.background} />
                                <input type="text" className='text-black rounded-e-lg bg-[#E8EBF2] p-1 text-sm focus:outline-none' name="" id="" onBlur={(e) => onBlockBgColorChange(e)} defaultValue={pageData?.blocksOption?.background} key={"input-bg-block-" + pageData?.blocksOption?.background} />
                            </div>
                        </div>}
                        {pageData?.blocksOption?.border != '0px' && <div>
                            <p className='text-sm font-semibold mb-1 text-[#273144]'>Block Border</p>
                            <div className='flex'>
                                <input type="color" className='h-8 w-8 rounded-s-lg outline-none border-none focus:outline-none focus:border-none' name="" id="" onChange={_debounce((e) => onBlockBorderColorChange(e), 300)} defaultValue={pageData?.blocksOption?.borderColor} key={pageData?.blocksOption?.borderColor} />
                                <input type="text" className='text-black rounded-e-lg bg-[#E8EBF2] p-1 text-sm focus:outline-none' name="" id="" onBlur={(e) => onBlockBorderColorChange(e)} defaultValue={pageData?.blocksOption?.borderColor} key={"input-bg-block-" + pageData?.blocksOption?.borderColor} />
                            </div>
                        </div>}
                    </div>
                </div>

                {/* fonts */}
                <div className="border border-1 border-[#ECECF2] p-2 rounded-xl mb-3">
                    <p className='text-sm font-bold'>Fonts</p>

                    <div className='flex flex-wrap gap-3'>
                        {
                            fonts.map((item) => {
                                return (
                                    <button key={item?.id} className={`${pageData?.textOption?.font == item?.name && 'border-2 border-primary rounded-xl overflow-hidden'}`} onClick={() => onFontSelect(item)}>
                                        <img src={process.env.PUBLIC_URL + `/images/page-layouts/font-${item?.id}.png`} alt="" />
                                    </button>
                                )
                            })
                        }
                    </div>
                </div>

            </div>
        </>
    )
}

export default CardDesignTab;